import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
} from '@ads/components-react';
import { ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Props de `ParcelCreateTemplate`. */
export interface ParcelCreateTemplateProps {
  /** ID do contrato como veio da rota — usado nos links do breadcrumb. */
  readonly financialPersonId: string;
  /** Nome do aluno no breadcrumb. */
  readonly studentName: string;
  /** Curso e turma, no subtítulo. */
  readonly subtitle: string;
  /** Seções do formulário. */
  readonly children: ReactNode;
  /** Dispara a criação. */
  readonly onSave: () => void;
  /** Descarta e volta para as parcelas. */
  readonly onCancel: () => void;
  readonly isSaving: boolean;
  /**
   * Envio liberado.
   *
   * Depende do preenchimento: `typeId` é obrigatório, e o botão fica bloqueado
   * enquanto o operador não escolhe — em vez de enviar um `POST` que o backend
   * recusaria. O motivo aparece no rodapé e no campo.
   */
  readonly canSave: boolean;
  /** Explicação do bloqueio, exibida no rodapé quando `canSave` é `false`. */
  readonly blockedReason?: string | undefined;
}

/**
 * Casca da tela de criação de parcela.
 *
 * Espelha a tela de edição de plano: breadcrumb, cabeçalho com título e ação de
 * volta, seções em cards e régua Cancelar/Salvar fora dos cards.
 *
 * Havia aqui um alerta no topo sobre a falta de lookup do tipo de parcela. Saiu
 * quando `shared/parcel-enums` passou a listar os tipos — o que resta é a validação
 * normal de campo obrigatório, que o rodapé já comunica.
 *
 * Segue o frame `twrEH` de `design/payment.pen`.
 */
export function ParcelCreateTemplate({
  financialPersonId,
  studentName,
  subtitle,
  children,
  onSave,
  onCancel,
  isSaving,
  canSave,
  blockedReason,
}: ParcelCreateTemplateProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-col gap-mini">
        {/* Só o último segmento é `BreadcrumbPage`: quem emite o ">" é o
            `BreadcrumbLink`, então um `BreadcrumbPage` no meio engole o separador. */}
        <Breadcrumb>
          <BreadcrumbLink asChild>
            <Link to="/">
              <BreadcrumbIcon />
            </Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link to="/individual-payment">{t('planEdit.breadcrumb.root')}</Link>
          </BreadcrumbLink>
          <BreadcrumbLink asChild>
            <Link
              to="/individual-payment/$financialPersonId/parcels"
              params={{ financialPersonId }}
            >
              {studentName}
            </Link>
          </BreadcrumbLink>
          <BreadcrumbPage>{t('parcelCreate.breadcrumb.current')}</BreadcrumbPage>
        </Breadcrumb>

        <div className="flex flex-wrap items-center gap-mini">
          <div className="flex flex-1 flex-col gap-quark">
            <h1 className="ts-heading-md text-ctx-content-title">{t('parcelCreate.title')}</h1>
            <p className="ts-paragraph-xs text-ctx-content-placeholder">{subtitle}</p>
          </div>
          <Button hierarchy="secondary" size="md" leadingIcon={ArrowLeftIcon} onClick={onCancel}>
            {t('parcelCreate.backToParcels')}
          </Button>
        </div>
      </div>

      {children}

      <div className="flex flex-wrap items-center gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-mini">
        {/* O motivo do bloqueio vive ao lado do botão bloqueado: um Salvar
            desabilitado sem explicação é lido como falta de permissão. */}
        {blockedReason === undefined ? null : (
          <span className="flex flex-1 items-center justify-start gap-nano">
            <InformationCircleIcon className="size-xxs shrink-0 text-ctx-content-placeholder" />
            <span className="ts-paragraph-xxs text-ctx-content-placeholder">{blockedReason}</span>
          </span>
        )}
        <span className="flex flex-1 justify-end gap-mini">
          <Button hierarchy="secondary" size="md" disabled={isSaving} onClick={onCancel}>
            {t('parcelCreate.footer.cancel')}
          </Button>
          <Button
            hierarchy="primary"
            size="md"
            isLoading={isSaving}
            disabled={isSaving || !canSave}
            onClick={onSave}
          >
            {isSaving ? t('parcelCreate.footer.saving') : t('parcelCreate.footer.save')}
          </Button>
        </span>
      </div>
    </div>
  );
}
